import React, { useState, useEffect, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { usePersistedState } from '../../hooks/usePersistedState'
import { useHospitalData } from '../../hooks/useHospitalData'
import LineChart, { LineChartHandle } from '../../components/Charts/LineChart'
import CustomSelect from '../../components/Common/CustomSelect'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import { sortTimePeriods, trimTrailingEmptyPeriods, filterFutureMonths, createChartExportHandlers, createMenuToggle, setupMenuClickOutside } from '../../utils/exportUtils'
import { generatePDFIds } from '../../utils/pdfIdHelper'
import './OutcomeMeasures.css'

const OutcomeMeasures: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const { data: treatmentData, loading: loadingTreatment, initialLoading: initialLoadingTreatment } = useHospitalData(
        '/api/pmh/outcome/pmhTreatment',
        selectedHospitalIds
    )

    const { data: medicationData, loading: loadingMedication, initialLoading: initialLoadingMedication } = useHospitalData(
        '/api/pmh/outcome/pmhmedication',
        selectedHospitalIds
    )

    const { data: linkageData, loading: loadingLinkage, initialLoading: initialLoadingLinkage } = useHospitalData(
        '/api/pmh/outcome/pmhlinkage',
        selectedHospitalIds
    )

    const { data: postpartumData, loading: loadingPostpartum, initialLoading: initialLoadingPostpartum } = useHospitalData(
        '/api/pmh/outcome/obpostpartum',
        selectedHospitalIds
    )

    const safeParseEducationPct = (value: any): number | null => {
        if (value === null || value === undefined) {
            return null
        }

        if (typeof value === 'string' && value.trim() === '') {
            return null
        }

        if (value === 0 || value === "0") {
            return 0
        }

        const num = parseFloat(value)
        return isNaN(num) ? null : num
    }

    const [interval1, setInterval1] = usePersistedState('pmh_outcome_interval1', 'monthly')
    const [chart1Data, setChart1Data] = useState<{ categories: string[], series: any[] }>({ categories: [], series: [] })
    const [chart1NoDataMessage, setChart1NoDataMessage] = useState<string | undefined>(undefined)

    const [interval2, setInterval2] = usePersistedState('pmh_outcome_interval2', 'monthly')
    const [chart2Data, setChart2Data] = useState<{ categories: string[], series: any[] }>({ categories: [], series: [] })
    const [chart2NoDataMessage, setChart2NoDataMessage] = useState<string | undefined>(undefined)

    const [interval3, setInterval3] = usePersistedState('pmh_outcome_interval3', 'monthly')
    const [stratType3, setStratType3] = usePersistedState('pmh_outcome_stratType3', 'insurance')
    const [chart3Data, setChart3Data] = useState<{ categories: string[], series: any[], colors: string[] }>({ categories: [], series: [], colors: [] })
    const [chart3NoDataMessage, setChart3NoDataMessage] = useState<string | undefined>(undefined)

    const [interval4, setInterval4] = usePersistedState('pmh_outcome_interval4', 'monthly')
    const [chart4Data, setChart4Data] = useState<{ categories: string[], series: any[] }>({ categories: [], series: [] })
    const [chart4NoDataMessage, setChart4NoDataMessage] = useState<string | undefined>(undefined)

    const [interval5, setInterval5] = usePersistedState('pmh_outcome_interval5', 'monthly')
    const [stratType5, setStratType5] = usePersistedState('pmh_outcome_stratType5', 'insurance')
    const [chart5Data, setChart5Data] = useState<{ categories: string[], series: any[], colors: string[] }>({ categories: [], series: [], colors: [] })
    const [chart5NoDataMessage, setChart5NoDataMessage] = useState<string | undefined>(undefined)

    const [interval6, setInterval6] = usePersistedState('pmh_outcome_interval6', 'monthly')
    const [chart6Data, setChart6Data] = useState<{ categories: string[], series: any[] }>({ categories: [], series: [] })
    const [chart6NoDataMessage, setChart6NoDataMessage] = useState<string | undefined>(undefined)

    const chart1Ref = useRef<LineChartHandle>(null)
    const chart1ContainerRef = useRef<HTMLDivElement>(null)
    const chart2Ref = useRef<LineChartHandle>(null)
    const chart2ContainerRef = useRef<HTMLDivElement>(null)
    const chart3Ref = useRef<LineChartHandle>(null)
    const chart3ContainerRef = useRef<HTMLDivElement>(null)
    const chart4Ref = useRef<LineChartHandle>(null)
    const chart4ContainerRef = useRef<HTMLDivElement>(null)
    const chart5Ref = useRef<LineChartHandle>(null)
    const chart5ContainerRef = useRef<HTMLDivElement>(null)
    const chart6Ref = useRef<LineChartHandle>(null)
    const chart6ContainerRef = useRef<HTMLDivElement>(null)

    const menu1Id = 'export-menu-outcome-1'
    const toggleMenu1 = createMenuToggle(menu1Id)
    const chart1Exports = createChartExportHandlers(chart1ContainerRef, 'PMH Treatment', 'PMH_Treatment_Overall', { fontBoost: true, exportTitle: 'PMH Treatment' })

    const menu2Id = 'export-menu-outcome-2'
    const toggleMenu2 = createMenuToggle(menu2Id)
    const chart2Exports = createChartExportHandlers(chart2ContainerRef, 'PMH Medication', 'PMH_Medication_Overall', { fontBoost: true, exportTitle: 'PMH Medication' })

    const menu3Id = 'export-menu-outcome-3'
    const toggleMenu3 = createMenuToggle(menu3Id)
    const chart3Exports = createChartExportHandlers(chart3ContainerRef, 'PMH Treatment - Stratified', 'PMH_Treatment_Stratified', { fontBoost: true, exportTitle: 'PMH Treatment - Stratified' })

    const menu4Id = 'export-menu-outcome-4'
    const toggleMenu4 = createMenuToggle(menu4Id)
    const chart4Exports = createChartExportHandlers(chart4ContainerRef, 'PMH Linkage To Follow Up', 'PMH_Linkage_Overall', { fontBoost: true, exportTitle: 'PMH Linkage To Follow Up' })

    const menu5Id = 'export-menu-outcome-5'
    const toggleMenu5 = createMenuToggle(menu5Id)
    const chart5Exports = createChartExportHandlers(chart5ContainerRef, 'PMH Linkage To Follow Up - Stratified', 'PMH_Linkage_Stratified', { fontBoost: true, exportTitle: 'PMH Linkage To Follow Up - Stratified' })

    const menu6Id = 'export-menu-outcome-6'
    const toggleMenu6 = createMenuToggle(menu6Id)
    const chart6Exports = createChartExportHandlers(chart6ContainerRef, 'Early OB Postpartum Follow-Up', 'Early_OB_Postpartum_FollowUp', { fontBoost: true, exportTitle: 'Early OB Postpartum Follow-Up' })

    useEffect(() => {
        const cleanup1 = setupMenuClickOutside(() => chart1Exports.closeMenu(menu1Id));
        const cleanup2 = setupMenuClickOutside(() => chart2Exports.closeMenu(menu2Id));
        const cleanup3 = setupMenuClickOutside(() => chart3Exports.closeMenu(menu3Id));
        const cleanup4 = setupMenuClickOutside(() => chart4Exports.closeMenu(menu4Id));
        const cleanup5 = setupMenuClickOutside(() => chart5Exports.closeMenu(menu5Id));
        const cleanup6 = setupMenuClickOutside(() => chart6Exports.closeMenu(menu6Id));
        return () => {
            cleanup1(); cleanup2(); cleanup3(); cleanup4(); cleanup5(); cleanup6();
        };
    }, []);

    const processTreatmentData = (data: any, interval: string) => {
        if (!data) return { categories: [], series: [], noDataMessage: "No Data Available" }

        const rows = data?.response?.result?.rows || []

        if (rows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly', 'quarterly': 'Quarterly', 'bi-yearly': 'Bi-Yearly', 'yearly': 'Yearly'
        }

        const filteredRows = rows.filter((row: any[]) => {
            return row[2] === intervalMap[interval] && row[3] === 'All'
        })

        if (filteredRows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        const periodData: Record<string, { medOrTherapy: number[], medOnly: number[], therapyOnly: number[] }> = {}

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[1])
            const medOrTherapy = safeParseEducationPct(row[8])
            const medOnly = safeParseEducationPct(row[9])
            const therapyOnly = safeParseEducationPct(row[10])

            if (!periodData[timePeriod]) {
                periodData[timePeriod] = { medOrTherapy: [], medOnly: [], therapyOnly: [] }
            }

            if (medOrTherapy !== null) periodData[timePeriod].medOrTherapy.push(medOrTherapy)
            if (medOnly !== null) periodData[timePeriod].medOnly.push(medOnly)
            if (therapyOnly !== null) periodData[timePeriod].therapyOnly.push(therapyOnly)
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Object.keys(periodData), interval))

        const series1: (number | null)[] = []
        const series2: (number | null)[] = []
        const series3: (number | null)[] = []

        sortedPeriods.forEach(period => {
            const d = periodData[period]

            const val1 = d.medOrTherapy.length ? d.medOrTherapy.reduce((a, b) => a + b, 0) / d.medOrTherapy.length : null
            const val2 = d.medOnly.length ? d.medOnly.reduce((a, b) => a + b, 0) / d.medOnly.length : null
            const val3 = d.therapyOnly.length ? d.therapyOnly.reduce((a, b) => a + b, 0) / d.therapyOnly.length : null

            series1.push(val1 !== null ? Math.round(val1 * 10) / 10 : null)
            series2.push(val2 !== null ? Math.round(val2 * 10) / 10 : null)
            series3.push(val3 !== null ? Math.round(val3 * 10) / 10 : null)
        })

        const hasValidData = series1.some(v => v !== null) || series2.some(v => v !== null) || series3.some(v => v !== null)

        if (!hasValidData) {
            return {
                categories: sortedPeriods,
                series: [
                    { name: 'Medication and/or Therapy', data: sortedPeriods.map(() => null) },
                    { name: 'Medication Only', data: sortedPeriods.map(() => null) },
                    { name: 'Therapy Only', data: sortedPeriods.map(() => null) }
                ],
                noDataMessage: "No Data Available"
            }
        }

        return {
            categories: sortedPeriods,
            series: [
                { name: 'Medication and/or Therapy', data: series1 },
                { name: 'Medication Only', data: series2 },
                { name: 'Therapy Only', data: series3 }
            ],
            noDataMessage: undefined
        }
    }

    const processMedicationData = (data: any, interval: string) => {
        if (!data) return { categories: [], series: [], noDataMessage: "No Data Available" }

        const rows = data?.response?.result?.rows || []
        const columns = data?.response?.result?.column_order || []

        if (rows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        const findIdx = (name: string, fb: number) => {
            const i = columns.indexOf(name); return i !== -1 ? i : fb
        }
        const idxMedication = findIdx('Pct_Medication_Provided', 6)
        const idxDeclined = findIdx('Pct_Counseling_Declined', 7)
        const idxDenom = findIdx('Denominator', 3)

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly', 'quarterly': 'Quarterly', 'bi-yearly': 'Bi-Yearly', 'yearly': 'Yearly'
        }

        // This table has no StratGroup column, so every row is already an overall figure.
        const filteredRows = rows.filter((row: any[]) => {
            return row[2] === intervalMap[interval]
        })

        if (filteredRows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        // Percentages are weighted by each hospital's denominator so that a multi-select
        // rollup is the pooled rate, matching how the measure aggregates over time.
        const periodData: Record<string, { medValue: number, medWeight: number, decValue: number, decWeight: number }> = {}
        const allPeriods = new Set<string>()

        filteredRows.forEach((row: any[]) => {
            if (row[1]) allPeriods.add(String(row[1]))
        })

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[1])
            const denom = safeParseEducationPct(row[idxDenom])
            if (denom === null || denom <= 0) return

            const medication = safeParseEducationPct(row[idxMedication])
            const declined = safeParseEducationPct(row[idxDeclined])

            if (!periodData[timePeriod]) {
                periodData[timePeriod] = { medValue: 0, medWeight: 0, decValue: 0, decWeight: 0 }
            }

            if (medication !== null) {
                periodData[timePeriod].medValue += medication * denom
                periodData[timePeriod].medWeight += denom
            }
            if (declined !== null) {
                periodData[timePeriod].decValue += declined * denom
                periodData[timePeriod].decWeight += denom
            }
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Array.from(allPeriods), interval))

        const series1: (number | null)[] = []
        const series2: (number | null)[] = []

        sortedPeriods.forEach(period => {
            const d = periodData[period]

            const val1 = d && d.medWeight > 0 ? d.medValue / d.medWeight : null
            const val2 = d && d.decWeight > 0 ? d.decValue / d.decWeight : null

            series1.push(val1 !== null ? Math.round(val1 * 10) / 10 : null)
            series2.push(val2 !== null ? Math.round(val2 * 10) / 10 : null)
        })

        const hasValidData = series1.some(v => v !== null) || series2.some(v => v !== null)

        if (!hasValidData) {
            return {
                categories: sortedPeriods,
                series: [
                    { name: 'Medication Provided', data: sortedPeriods.map(() => null) },
                    { name: 'Counseling Documented but Declined', data: sortedPeriods.map(() => null) }
                ],
                noDataMessage: "No Data Available"
            }
        }

        return {
            categories: sortedPeriods,
            series: [
                { name: 'Medication Provided', data: series1 },
                { name: 'Counseling Documented but Declined', data: series2 }
            ],
            noDataMessage: undefined
        }
    }

    const processLinkageData = (data: any, interval: string) => {
        if (!data) return { categories: [], series: [], noDataMessage: "No Data Available" }

        const rows = data?.response?.result?.rows || []

        if (rows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly', 'quarterly': 'Quarterly', 'bi-yearly': 'Bi-Yearly', 'yearly': 'Yearly'
        }

        const filteredRows = rows.filter((row: any[]) => {
            return row[2] === intervalMap[interval] && row[3] === 'All'
        })

        if (filteredRows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        const periodData: Record<string, { scheduled: number[], declined: number[], notSched: number[], warmHandoff: number[] }> = {}

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[1])
            const scheduled = safeParseEducationPct(row[9])
            const declined = safeParseEducationPct(row[10])
            const notSched = safeParseEducationPct(row[11])
            const warmHandoff = safeParseEducationPct(row[12])

            if (!periodData[timePeriod]) {
                periodData[timePeriod] = { scheduled: [], declined: [], notSched: [], warmHandoff: [] }
            }

            if (scheduled !== null) periodData[timePeriod].scheduled.push(scheduled)
            if (declined !== null) periodData[timePeriod].declined.push(declined)
            if (notSched !== null) periodData[timePeriod].notSched.push(notSched)
            if (warmHandoff !== null) periodData[timePeriod].warmHandoff.push(warmHandoff)
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Object.keys(periodData), interval))
        const s1: (number | null)[] = []
        const s2: (number | null)[] = []
        const s3: (number | null)[] = []
        const s4: (number | null)[] = []

        sortedPeriods.forEach(period => {
            const d = periodData[period]
            const avg1 = d.scheduled.length ? d.scheduled.reduce((a, b) => a + b, 0) / d.scheduled.length : null
            const avg2 = d.declined.length ? d.declined.reduce((a, b) => a + b, 0) / d.declined.length : null
            const avg3 = d.notSched.length ? d.notSched.reduce((a, b) => a + b, 0) / d.notSched.length : null
            const avg4 = d.warmHandoff.length ? d.warmHandoff.reduce((a, b) => a + b, 0) / d.warmHandoff.length : null

            s1.push(avg1 !== null ? Math.round(avg1 * 10) / 10 : null)
            s2.push(avg2 !== null ? Math.round(avg2 * 10) / 10 : null)
            s3.push(avg3 !== null ? Math.round(avg3 * 10) / 10 : null)
            s4.push(avg4 !== null ? Math.round(avg4 * 10) / 10 : null)
        })

        const hasValidData = s1.some(v => v !== null) || s2.some(v => v !== null) || s3.some(v => v !== null) || s4.some(v => v !== null)

        if (!hasValidData) {
            return {
                categories: sortedPeriods,
                series: [
                    { name: 'Follow Up Scheduled', data: sortedPeriods.map(() => null) },
                    { name: 'Documented but Declined', data: sortedPeriods.map(() => null) },
                    { name: 'Referral Made But Not Scheduled', data: sortedPeriods.map(() => null) },
                    { name: 'Warm Handoff Provided', data: sortedPeriods.map(() => null) }
                ],
                noDataMessage: "No Data Available"
            }
        }

        return {
            categories: sortedPeriods,
            series: [
                { name: 'Follow Up Scheduled', data: s1 },
                { name: 'Documented but Declined', data: s2 },
                { name: 'Referral Made But Not Scheduled', data: s3 },
                { name: 'Warm Handoff Provided', data: s4 }
            ],
            noDataMessage: undefined
        }
    }

    const processPostpartumData = (data: any, interval: string) => {
        if (!data) return { categories: [], series: [], noDataMessage: "No Data Available" }

        const rows = data?.response?.result?.rows || []

        if (rows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly', 'quarterly': 'Quarterly', 'bi-yearly': 'Bi-Yearly', 'yearly': 'Yearly'
        }

        const filteredRows = rows.filter((row: any[]) => {
            return row[2] === intervalMap[interval]
        })

        if (filteredRows.length === 0) {
            return { categories: [], series: [], noDataMessage: "No Data Available" }
        }

        const periodData: Record<string, number[]> = {}

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[1])
            const val = safeParseEducationPct(row[5])

            if (!periodData[timePeriod]) periodData[timePeriod] = []
            if (val !== null) periodData[timePeriod].push(val)
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Object.keys(periodData), interval))
        const seriesData: (number | null)[] = []

        sortedPeriods.forEach(period => {
            const vals = periodData[period]
            const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null
            seriesData.push(avg !== null ? Math.round(avg * 10) / 10 : null)
        })

        const hasValidData = seriesData.some(v => v !== null)

        if (!hasValidData) {
            return {
                categories: sortedPeriods,
                series: [{ name: 'Early OB Postpartum Follow-Up Scheduled', data: sortedPeriods.map(() => null) }],
                noDataMessage: "No Data Available"
            }
        }

        return {
            categories: sortedPeriods,
            series: [{ name: 'Early OB Postpartum Follow-Up Scheduled', data: seriesData }],
            noDataMessage: undefined
        }
    }

    const processStratifiedData = (data: any, interval: string, stratType: string, valueColumnIndex: number = 8) => {
        if (!data) return { categories: [], series: [], colors: [], noDataMessage: "No Data Available" }

        const rows = data?.response?.result?.rows || []

        if (rows.length === 0) {
            return { categories: [], series: [], colors: [], noDataMessage: "No Data Available" }
        }

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly',
            'quarterly': 'Quarterly',
            'bi-yearly': 'Bi-Yearly',
            'yearly': 'Yearly'
        }

        const filteredRows = rows.filter((row: any[]) => {
            return row[2] === intervalMap[interval]
        })

        if (filteredRows.length === 0) {
            return { categories: [], series: [], colors: [], noDataMessage: "No Data Available" }
        }

        const groups: Record<string, Record<string, number[]>> = {}
        const allPeriods = new Set<string>()
        const valueIdx = valueColumnIndex

        filteredRows.forEach((row: any[]) => {
            if (row[1]) allPeriods.add(String(row[1]))
        })

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[1])
            const stratGroup = String(row[3])
            const val = safeParseEducationPct(row[valueIdx])

            if (val !== null) {
                let match = false

                if (stratType === 'insurance' && ['Private', 'Public'].includes(stratGroup)) match = true
                else if (stratType === 'language' && ['English', 'Other'].includes(stratGroup)) match = true
                else if ((stratType === 'bipoc' || stratType === 'race') &&
                    ['Asian', 'BIPOC', 'Black/African American', 'Hispanic', 'White', 'WHITE', 'Race-Other'].includes(stratGroup)) {
                    match = true
                }

                if (match) {
                    if (!groups[stratGroup]) groups[stratGroup] = {}
                    if (!groups[stratGroup][timePeriod]) groups[stratGroup][timePeriod] = []
                    groups[stratGroup][timePeriod].push(val)
                }
            }
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Array.from(allPeriods), interval))
        const series: any[] = []

        let seriesDefs: { name: string, keys: string[] }[] = []

        if (stratType === 'insurance') {
            seriesDefs = [
                { name: 'Private', keys: ['Private'] },
                { name: 'Public', keys: ['Public'] }
            ]
        } else if (stratType === 'bipoc') {
            seriesDefs = [
                { name: 'BIPOC', keys: ['BIPOC'] },
                { name: 'WHITE', keys: ['WHITE', 'White'] }
            ]
        } else if (stratType === 'race') {
            seriesDefs = [
                { name: 'Black/African American', keys: ['Black/African American'] },
                { name: 'White', keys: ['White', 'WHITE'] },
                { name: 'Hispanic', keys: ['Hispanic'] },
                { name: 'Asian', keys: ['Asian'] },
                { name: 'Other', keys: ['Race-Other'] }
            ]
        } else if (stratType === 'language') {
            seriesDefs = [
                { name: 'English', keys: ['English'] },
                { name: 'Other', keys: ['Other'] }
            ]
        } else {
            seriesDefs = Object.keys(groups).map(g => ({ name: g, keys: [g] }))
        }

        seriesDefs.forEach(def => {
            const seriesData = sortedPeriods.map(period => {
                for (const key of def.keys) {
                    const values = groups[key]?.[period]
                    if (values && values.length > 0) {
                        const validValues = values.filter(v => v !== null)
                        if (validValues.length > 0) {
                            const avg = validValues.reduce((a, b) => a + b, 0) / validValues.length
                            return Math.round(avg * 10) / 10
                        }
                    }
                }
                return null
            })

            const hasData = seriesData.some(v => v !== null)

            if (hasData || stratType === 'insurance') {
                series.push({ name: def.name, data: seriesData })
            }
        })

        const colors = ["#0072B2", "#D55E00", "#F0E441", "#009E73", "#cc79a7ff"]

        const allNull = series.length > 0 && series.every(s => s.data.every((v: any) => v === null))

        if (allNull) {
            return { categories: sortedPeriods, series, colors, noDataMessage: "No Data Available" }
        }

        return {
            categories: sortedPeriods,
            series,
            colors: colors,
            noDataMessage: undefined
        }
    }

    const processLinkageStratifiedData = (data: any, interval: string, stratType: string) => {
        if (!data) return { categories: [], series: [], colors: [], noDataMessage: "No Data Available" }

        const rows = data?.response?.result?.rows || []
        const columns = data?.response?.result?.column_order || []
        if (rows.length === 0) return { categories: [], series: [], colors: [], noDataMessage: "No Data Available" }

        const findIdx = (name: string, fb: number) => {
            const i = columns.indexOf(name); return i !== -1 ? i : fb
        }
        const idxFollowUp = findIdx('Num_FollowUpScheduled', 5)
        const idxDeclined = findIdx('Num_Declined', 6)
        const idxReferral = findIdx('Num_ReferralNotSched', 7)
        const idxWarm = findIdx('Num_WarmHandoff', 8)
        const idxDenom = findIdx('Denominator', 4)

        const intervalMap: Record<string, string> = {
            'monthly': 'Monthly', 'quarterly': 'Quarterly', 'bi-yearly': 'Bi-Yearly', 'yearly': 'Yearly'
        }
        const filteredRows = rows.filter((row: any[]) => row[2] === intervalMap[interval])
        if (filteredRows.length === 0 || idxDenom === -1) {
            return { categories: [], series: [], colors: [], noDataMessage: "No Data Available" }
        }

        const groups: Record<string, Record<string, { num: number, denom: number }>> = {}
        const allPeriods = new Set<string>()
        filteredRows.forEach((row: any[]) => { if (row[1]) allPeriods.add(String(row[1])) })

        filteredRows.forEach((row: any[]) => {
            const timePeriod = String(row[1])
            const stratGroup = String(row[3])
            const denom = safeParseEducationPct(row[idxDenom])
            if (denom === null) return

            const numSum =
                (safeParseEducationPct(row[idxFollowUp]) ?? 0) +
                (safeParseEducationPct(row[idxDeclined]) ?? 0) +
                (safeParseEducationPct(row[idxReferral]) ?? 0) +
                (safeParseEducationPct(row[idxWarm]) ?? 0)

            let match = false
            if (stratType === 'insurance' && ['Private', 'Public'].includes(stratGroup)) match = true
            else if (stratType === 'language' && ['English', 'Other'].includes(stratGroup)) match = true
            else if ((stratType === 'bipoc' || stratType === 'race') &&
                ['Asian', 'BIPOC', 'Black/African American', 'Hispanic', 'White', 'WHITE', 'Race-Other'].includes(stratGroup)) match = true

            if (match) {
                if (!groups[stratGroup]) groups[stratGroup] = {}
                if (!groups[stratGroup][timePeriod]) groups[stratGroup][timePeriod] = { num: 0, denom: 0 }
                groups[stratGroup][timePeriod].num += numSum
                groups[stratGroup][timePeriod].denom += denom
            }
        })

        const sortedPeriods = filterFutureMonths(sortTimePeriods(Array.from(allPeriods), interval))
        const series: any[] = []
        let seriesDefs: { name: string, keys: string[] }[] = []

        if (stratType === 'insurance') {
            seriesDefs = [{ name: 'Private', keys: ['Private'] }, { name: 'Public', keys: ['Public'] }]
        } else if (stratType === 'bipoc') {
            seriesDefs = [{ name: 'BIPOC', keys: ['BIPOC'] }, { name: 'WHITE', keys: ['WHITE', 'White'] }]
        } else if (stratType === 'race') {
            seriesDefs = [
                { name: 'Black/African American', keys: ['Black/African American'] },
                { name: 'White', keys: ['White', 'WHITE'] },
                { name: 'Hispanic', keys: ['Hispanic'] },
                { name: 'Asian', keys: ['Asian'] },
                { name: 'Other', keys: ['Race-Other'] }
            ]
        } else if (stratType === 'language') {
            seriesDefs = [{ name: 'English', keys: ['English'] }, { name: 'Other', keys: ['Other'] }]
        } else {
            seriesDefs = Object.keys(groups).map(g => ({ name: g, keys: [g] }))
        }

        seriesDefs.forEach(def => {
            const seriesData = sortedPeriods.map(period => {
                for (const key of def.keys) {
                    const agg = groups[key]?.[period]
                    if (agg && agg.denom > 0) return Math.round((agg.num / agg.denom * 100) * 10) / 10
                }
                return null
            })
            const hasData = seriesData.some(v => v !== null)
            if (hasData || stratType === 'insurance') series.push({ name: def.name, data: seriesData })
        })

        const colors = ["#0072B2", "#D55E00", "#F0E441", "#009E73", "#cc79a7ff"]
        const allNull = series.length > 0 && series.every(s => s.data.every((v: any) => v === null))
        if (allNull) return { categories: sortedPeriods, series, colors, noDataMessage: "No Data Available" }

        return { categories: sortedPeriods, series, colors, noDataMessage: undefined }
    }

    useEffect(() => {
        const result = processTreatmentData(treatmentData, interval1)
        if (result.noDataMessage) {
            setChart1Data({ categories: result.categories, series: result.series })
        } else {
            const trimmed = trimTrailingEmptyPeriods(result.categories, result.series)
            setChart1Data({ categories: trimmed.categories, series: trimmed.series })
        }
        setChart1NoDataMessage(result.noDataMessage)
    }, [treatmentData, interval1, isMultiSelect])

    useEffect(() => {
        const result = processMedicationData(medicationData, interval2)
        if (result.noDataMessage) {
            setChart2Data({ categories: result.categories, series: result.series })
        } else {
            const trimmed = trimTrailingEmptyPeriods(result.categories, result.series)
            setChart2Data({ categories: trimmed.categories, series: trimmed.series })
        }
        setChart2NoDataMessage(result.noDataMessage)
    }, [medicationData, interval2, isMultiSelect])

    useEffect(() => {
        const result = processStratifiedData(treatmentData, interval3, stratType3)
        if (result.noDataMessage) {
            setChart3Data({ categories: result.categories, series: result.series, colors: result.colors })
        } else {
            const trimmed = trimTrailingEmptyPeriods(result.categories, result.series)
            setChart3Data({ categories: trimmed.categories, series: trimmed.series, colors: result.colors })
        }
        setChart3NoDataMessage(result.noDataMessage)
    }, [treatmentData, interval3, stratType3, isMultiSelect])

    useEffect(() => {
        const result = processLinkageData(linkageData, interval4)
        if (result.noDataMessage) {
            setChart4Data({ categories: result.categories, series: result.series })
        } else {
            const trimmed = trimTrailingEmptyPeriods(result.categories, result.series)
            setChart4Data({ categories: trimmed.categories, series: trimmed.series })
        }
        setChart4NoDataMessage(result.noDataMessage)
    }, [linkageData, interval4, isMultiSelect])

    useEffect(() => {
        const result = processLinkageStratifiedData(linkageData, interval5, stratType5)
        if (result.noDataMessage) {
            setChart5Data({ categories: result.categories, series: result.series, colors: result.colors })
        } else {
            const trimmed = trimTrailingEmptyPeriods(result.categories, result.series)
            setChart5Data({ categories: trimmed.categories, series: trimmed.series, colors: result.colors })
        }
        setChart5NoDataMessage(result.noDataMessage)
    }, [linkageData, interval5, stratType5, isMultiSelect])

    useEffect(() => {
        const result = processPostpartumData(postpartumData, interval6)
        if (result.noDataMessage) {
            setChart6Data({ categories: result.categories, series: result.series })
        } else {
            const trimmed = trimTrailingEmptyPeriods(result.categories, result.series)
            setChart6Data({ categories: trimmed.categories, series: trimmed.series })
        }
        setChart6NoDataMessage(result.noDataMessage)
    }, [postpartumData, interval6, isMultiSelect])


    if (initialLoadingTreatment || initialLoadingMedication || initialLoadingLinkage || initialLoadingPostpartum) return <LoadingNotice title="Loading outcome measures data..." />

    return (
        <div id={generatePDFIds.dashboard()} className="pmh-outcome-measures-page">
            <div className="page-header">
                <h1 className="page-title">Outcome Measures</h1>
                <div className="header-controls">
                    <CustomSelect
                        value={interval1}
                        onChange={setInterval1}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' }
                        ]}
                    />
                </div>
            </div>

            <div id={generatePDFIds.chart(1)} className="chart-section" ref={chart1ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">PMH Treatment</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu1}>&#9776;</button>
                        <div id={menu1Id} className="export-dropdown">
                            <div onClick={() => { chart1Exports.handleDownloadSVG(menu1Id); }}>Download SVG</div>
                            <div onClick={() => { chart1Exports.handleDownloadPNG(menu1Id); }}>Download PNG</div>
                            <div onClick={() => { chart1Ref.current?.exportToCSV(); chart1Exports.closeMenu(menu1Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patients provided treatment (medication and/or therapy) for PMH condition prenatally or by delivery discharge? (Counseled, received, and/or referred)
                </p>
                <LineChart
                    ref={chart1Ref}
                    categories={chart1Data.categories}
                    series={chart1Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="PMH Treatment %"
                    xAxisLabel="Time Period"
                    colors={["#0072B2", "#D55E00", "#F0E441"]}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={chart1NoDataMessage}
                    showXAxisTooltip={true}
                    exportFilename="PMH_Treatment_Overall"
                    showToolbar={false}
                    isLoading={loadingTreatment}
                    decimals={1}
                />
                <p className="data-source">Data from patient level data form</p>
            </div>

            <div className="interval-controls-container">
                <CustomSelect
                    value={interval2}
                    onChange={setInterval2}
                    options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'bi-yearly', label: 'Bi-Yearly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                />
            </div>

            <div id={generatePDFIds.chart(2)} className="chart-section" ref={chart2ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">PMH Medication</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu2}>&#9776;</button>
                        <div id={menu2Id} className="export-dropdown">
                            <div onClick={() => { chart2Exports.handleDownloadSVG(menu2Id); }}>Download SVG</div>
                            <div onClick={() => { chart2Exports.handleDownloadPNG(menu2Id); }}>Download PNG</div>
                            <div onClick={() => { chart2Ref.current?.exportToCSV(); chart2Exports.closeMenu(menu2Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patients counseled on or provided medication for PMH conditions prenatally or by delivery discharge
                </p>
                <LineChart
                    ref={chart2Ref}
                    categories={chart2Data.categories}
                    series={chart2Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="PMH Medication %"
                    xAxisLabel="Time Period"
                    colors={["#0072B2", "#D55E00"]}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={chart2NoDataMessage}
                    showXAxisTooltip={true}
                    exportFilename="PMH_Medication_Overall"
                    showToolbar={false}
                    isLoading={loadingMedication}
                    decimals={1}
                />
                <p className="data-source">Data from patient level data form</p>
            </div>

            <div className="strat-controls-container">
                <CustomSelect
                    value={stratType3}
                    onChange={setStratType3}
                    options={[
                        { value: 'insurance', label: 'Insurance Status' },
                        { value: 'bipoc', label: 'Race/Ethnicity (BIPOC & WHITE)' },
                        { value: 'race', label: 'Race/Ethnicity' },
                        { value: 'language', label: 'Primary Language' }
                    ]}
                />
                <CustomSelect
                    value={interval3}
                    onChange={setInterval3}
                    options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'bi-yearly', label: 'Bi-Yearly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                />
            </div>

            <div id={generatePDFIds.chart(3)} className="chart-section" ref={chart3ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">PMH Treatment - Stratified</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu3}>&#9776;</button>
                        <div id={menu3Id} className="export-dropdown">
                            <div onClick={() => { chart3Exports.handleDownloadSVG(menu3Id); }}>Download SVG</div>
                            <div onClick={() => { chart3Exports.handleDownloadPNG(menu3Id); }}>Download PNG</div>
                            <div onClick={() => { chart3Ref.current?.exportToCSV(); chart3Exports.closeMenu(menu3Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patients provided treatment (medication and/or therapy) for PMH condition prenatally or by delivery discharge (Counseled, received, and/or referred) stratified by {stratType3}.
                </p>
                <LineChart
                    ref={chart3Ref}
                    categories={chart3Data.categories}
                    series={chart3Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="PMH Treatment %"
                    xAxisLabel="Time Period"
                    colors={chart3Data.colors}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={chart3NoDataMessage}
                    showXAxisTooltip={true}
                    exportFilename="PMH_Treatment_Stratified"
                    showToolbar={false}
                    isLoading={loadingTreatment}
                    decimals={1}
                />
                <p className="data-source">Data from patient level data form</p>
            </div>

            <div className="interval-controls-container">
                <CustomSelect
                    value={interval4}
                    onChange={setInterval4}
                    options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'bi-yearly', label: 'Bi-Yearly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                />
            </div>

            <div id={generatePDFIds.chart(4)} className="chart-section" ref={chart4ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">PMH Linkage To Follow Up</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu4}>&#9776;</button>
                        <div id={menu4Id} className="export-dropdown">
                            <div onClick={() => { chart4Exports.handleDownloadSVG(menu4Id); }}>Download SVG</div>
                            <div onClick={() => { chart4Exports.handleDownloadPNG(menu4Id); }}>Download PNG</div>
                            <div onClick={() => { chart4Ref.current?.exportToCSV(); chart4Exports.closeMenu(menu4Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patients provided behavioral health counseling/support options with follow up appointment scheduled prenatally or by delivery discharge?
                </p>
                <LineChart
                    ref={chart4Ref}
                    categories={chart4Data.categories}
                    series={chart4Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="PMH Linkage To Follow up %"
                    xAxisLabel="Time Period"
                    colors={["#0072B2", "#D55E00", "#F0E441", "#009E73"]}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={chart4NoDataMessage}
                    showXAxisTooltip={true}
                    exportFilename="PMH_Linkage_Overall"
                    showToolbar={false}
                    isLoading={loadingLinkage}
                    decimals={1}
                />
                <p className="data-source">Data from patient level data form</p>
            </div>

            <div className="strat-controls-container">
                <CustomSelect
                    value={stratType5}
                    onChange={setStratType5}
                    options={[
                        { value: 'insurance', label: 'Insurance Status' },
                        { value: 'bipoc', label: 'Race/Ethnicity (BIPOC & WHITE)' },
                        { value: 'race', label: 'Race/Ethnicity' },
                        { value: 'language', label: 'Primary Language' }
                    ]}
                />
                <CustomSelect
                    value={interval5}
                    onChange={setInterval5}
                    options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'bi-yearly', label: 'Bi-Yearly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                />
            </div>

            <div id={generatePDFIds.chart(5)} className="chart-section" ref={chart5ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">PMH Linkage To Follow Up - Stratified</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu5}>&#9776;</button>
                        <div id={menu5Id} className="export-dropdown">
                            <div onClick={() => { chart5Exports.handleDownloadSVG(menu5Id); }}>Download SVG</div>
                            <div onClick={() => { chart5Exports.handleDownloadPNG(menu5Id); }}>Download PNG</div>
                            <div onClick={() => { chart5Ref.current?.exportToCSV(); chart5Exports.closeMenu(menu5Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patients provided behavioral health counseling/support options with follow up appointment scheduled prenatally or by delivery discharge stratified by {stratType5}.
                </p>
                <LineChart
                    ref={chart5Ref}
                    categories={chart5Data.categories}
                    series={chart5Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="PMH Linkage To Follow up %"
                    xAxisLabel="Time Period"
                    colors={chart5Data.colors}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={chart5NoDataMessage}
                    showXAxisTooltip={true}
                    exportFilename="PMH_Linkage_Stratified"
                    showToolbar={false}
                    isLoading={loadingLinkage}
                    decimals={1}
                />
                <p className="data-source">Data from patient level data form</p>
            </div>

            <div className="interval-controls-container">
                <CustomSelect
                    value={interval6}
                    onChange={setInterval6}
                    options={[
                        { value: 'monthly', label: 'Monthly' },
                        { value: 'quarterly', label: 'Quarterly' },
                        { value: 'bi-yearly', label: 'Bi-Yearly' },
                        { value: 'yearly', label: 'Yearly' }
                    ]}
                />
            </div>

            <div id={generatePDFIds.chart(6)} className="chart-section" ref={chart6ContainerRef}>
                <div className="chart-title-wrapper">
                    <h2 className="section-title">Early OB Postpartum Follow-Up</h2>
                    <div className="menu-container export-menu-container" style={{ position: 'relative' }}>
                        <button className="hamburger" onClick={toggleMenu6}>&#9776;</button>
                        <div id={menu6Id} className="export-dropdown">
                            <div onClick={() => { chart6Exports.handleDownloadSVG(menu6Id); }}>Download SVG</div>
                            <div onClick={() => { chart6Exports.handleDownloadPNG(menu6Id); }}>Download PNG</div>
                            <div onClick={() => { chart6Ref.current?.exportToCSV(); chart6Exports.closeMenu(menu6Id); }}>Download CSV</div>
                        </div>
                    </div>
                </div>
                <p className="chart-description">
                    Percent of patients Scheduled for an early OB postpartum follow-up visit with a clinical Provider (by 2 weeks postpartum?)
                </p>
                <LineChart
                    ref={chart6Ref}
                    categories={chart6Data.categories}
                    series={chart6Data.series}
                    height={450}
                    goalLine={80}
                    goalLabel="Goal ≥ 80%"
                    min={0}
                    max={100}
                    tickAmount={20}
                    yAxisLabel="2 week OB followup %"
                    xAxisLabel="Time Period"
                    colors={["#0072B2"]}
                    legendPosition="top"
                    legendAlign="center"
                    noDataMessage={chart6NoDataMessage}
                    showXAxisTooltip={true}
                    exportFilename="Early_OB_Postpartum_FollowUp"
                    showToolbar={false}
                    isLoading={loadingPostpartum}
                    decimals={1}
                />
                <p className="data-source">Data from patient level data form</p>
            </div>

        </div>
    )
}

export default OutcomeMeasures
